import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { SharedBadge } from '../components/network/Badges';
import type { ProxyList, ProxyMethod, ProxySettings, ProxyTestResponse } from '../types';

/** Proxy lists: named sets of proxies, each assigned to the fetch methods it serves.
 *
 *  A method no enabled list serves fetches DIRECT from our network — that's a choice the
 *  admins make per method, not an error, which is why the coverage summary says so plainly.
 *  Every list is the network's: all users' fetches go through it. */

export const METHODS: { key: ProxyMethod; label: string; hint: string }[] = [
  { key: 'crawl4ai', label: 'crawl4ai', hint: 'One proxy per crawl batch.' },
  { key: 'http_pages', label: 'Plain HTTP - pages', hint: 'The cheapest page rung.' },
  {
    key: 'http_documents',
    label: 'Plain HTTP - documents',
    hint: 'Sitemaps, robots.txt, feeds.',
  },
];

const input =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

export default function ProxyListsSettings({ onChanged }: { onChanged?: () => void }) {
  const [lists, setLists] = useState<ProxyList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newUses, setNewUses] = useState<ProxyMethod[]>([]);

  const load = useCallback(async () => {
    try {
      setLists(await api.getProxyLists());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load proxy lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changed = async () => {
    await load();
    onChanged?.();
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    try {
      await fn();
      await changed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    }
  };

  const serving = (method: ProxyMethod) =>
    lists.filter((l) => l.is_enabled && l.uses.includes(method)).map((l) => l.name);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Coverage: what each method does right now. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {METHODS.map((m) => {
          const names = serving(m.key);
          return (
            <div key={m.key} className="rounded-md bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{m.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {names.length ? `Through ${names.join(', ')}` : 'Direct from our network'}
              </p>
            </div>
          );
        })}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {lists.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No proxy lists yet - every method fetches direct from our network.
            </p>
          )}
          {lists.map((l) => (
            <ListRow
              key={l.id}
              list={l}
              open={open === l.id}
              onToggleOpen={() => setOpen(open === l.id ? null : l.id)}
              onUpdate={(body) => run(() => api.updateProxyList(l.id, body))}
              onDelete={() => {
                if (
                  window.confirm(
                    `Delete "${l.name}" and its ${l.proxy_count} proxies? Methods it serves will fall back to other lists, or to fetching direct.`,
                  )
                ) {
                  run(() => api.deleteProxyList(l.id));
                }
              }}
              onProxiesChanged={changed}
            />
          ))}
        </div>
      )}

      {/* New list */}
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">New proxy list</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Residential"
            className={`${input} flex-1`}
          />
          <button
            type="button"
            disabled={!newName.trim()}
            onClick={() =>
              run(async () => {
                const created = await api.createProxyList({ name: newName.trim(), uses: newUses });
                setNewName('');
                setNewUses([]);
                setOpen(created.id);
              })
            }
            className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            Create
          </button>
        </div>
        <MethodChecks value={newUses} onChange={setNewUses} />
      </div>
    </div>
  );
}

function MethodChecks({
  value,
  onChange,
}: {
  value: ProxyMethod[];
  onChange: (next: ProxyMethod[]) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
      <span className="text-xs text-gray-500 dark:text-gray-400">Used for:</span>
      {METHODS.map((m) => (
        <label key={m.key} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200" title={m.hint}>
          <input
            type="checkbox"
            checked={value.includes(m.key)}
            onChange={() =>
              onChange(
                value.includes(m.key) ? value.filter((v) => v !== m.key) : [...value, m.key],
              )
            }
          />
          {m.label}
        </label>
      ))}
    </div>
  );
}

function ListRow({
  list,
  open,
  onToggleOpen,
  onUpdate,
  onDelete,
  onProxiesChanged,
}: {
  list: ProxyList;
  open: boolean;
  onToggleOpen: () => void;
  onUpdate: (body: { name?: string; is_enabled?: boolean; uses?: ProxyMethod[] }) => void;
  onDelete: () => void;
  onProxiesChanged: () => void;
}) {
  const [name, setName] = useState(list.name);
  useEffect(() => setName(list.name), [list.name]);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="px-3 py-2 flex flex-wrap items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name.trim() !== list.name && onUpdate({ name: name.trim() })}
          className="text-sm font-medium bg-transparent border-b border-transparent focus:border-gray-300 dark:focus:border-gray-600 focus:outline-none text-gray-900 dark:text-gray-100 min-w-0 flex-1"
          aria-label="List name"
        />
        <SharedBadge />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {list.enabled_count}/{list.proxy_count} on
          {list.cooling_count ? ` · ${list.cooling_count} down` : ''}
          {list.resting_somewhere_count ? ` · ${list.resting_somewhere_count} resting from some sites` : ''}
        </span>
        <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={list.is_enabled}
            onChange={(e) => onUpdate({ is_enabled: e.target.checked })}
          />
          Enabled
        </label>
        <button type="button" onClick={onToggleOpen} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
          {open ? 'Hide proxies' : 'Proxies'}
        </button>
        <button type="button" onClick={onDelete} className="text-xs text-red-600 dark:text-red-400 hover:underline">
          Delete
        </button>
      </div>
      <div className="px-3 pb-2">
        <MethodChecks value={list.uses} onChange={(uses) => onUpdate({ uses })} />
        {list.notes && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{list.notes}</p>}
      </div>
      {open && <ProxyTable listId={list.id} onChanged={onProxiesChanged} />}
    </div>
  );
}

function ProxyTable({ listId, onChanged }: { listId: number; onChanged: () => void }) {
  const [proxies, setProxies] = useState<ProxySettings[]>([]);
  const [paste, setPaste] = useState('');
  const [tests, setTests] = useState<Record<number, ProxyTestResponse | 'testing'>>({});
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setProxies((await api.getProxies(listId)).items);
  }, [listId]);

  useEffect(() => {
    load();
  }, [load]);

  const after = async () => {
    await load();
    onChanged();
  };

  const add = async () => {
    const res = await api.bulkAddProxies(listId, paste);
    setPaste('');
    setMessage(
      `Added ${res.added}` + (res.errors.length ? ` · ${res.errors.length} skipped: ${res.errors.join('; ')}` : ''),
    );
    await after();
  };

  const test = async (id: number) => {
    setTests((t) => ({ ...t, [id]: 'testing' }));
    const res = await api.testProxy(id);
    setTests((t) => ({ ...t, [id]: res }));
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-3 space-y-3">
      {proxies.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No proxies in this list yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400">
              <th className="py-1 font-medium">Proxy</th>
              <th className="py-1 font-medium">State</th>
              <th className="py-1 font-medium">Record</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {proxies.map((p) => {
              const t = tests[p.id];
              return (
                <tr key={p.id} className="text-gray-700 dark:text-gray-200">
                  <td className="py-1.5 font-mono">
                    {p.username ? `${p.username}@` : ''}
                    {p.server}:{p.port}
                  </td>
                  <td className="py-1.5">
                    {!p.is_enabled ? (
                      'Off'
                    ) : p.cooling_down ? (
                      'Down - the proxy itself failed; back shortly'
                    ) : p.resting_for.length ? (
                      <span title="Walled or timed out on these sites; serving every other site">
                        Ready · resting from{' '}
                        {p.resting_for
                          .map((r) => `${r.host} (${Math.max(1, Math.round(r.seconds_left / 60))}m)`)
                          .join(', ')}
                      </span>
                    ) : (
                      'Ready'
                    )}
                  </td>
                  <td className="py-1.5 text-gray-500 dark:text-gray-400">
                    {p.success_count} ok · {p.captcha_encountered_count} captchas
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap space-x-2">
                    <button type="button" onClick={() => test(p.id)} className="text-blue-600 dark:text-blue-400 hover:underline">
                      {t === 'testing' ? 'Testing…' : 'Test'}
                    </button>
                    {t && t !== 'testing' && (
                      <span className={t.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {t.ok ? `${t.response_time_ms}ms` : t.error}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        await api.setProxyEnabled(p.id, !p.is_enabled);
                        await after();
                      }}
                      className="text-gray-600 dark:text-gray-300 hover:underline"
                    >
                      {p.is_enabled ? 'Turn off' : 'Turn on'}
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm(`Delete ${p.server}:${p.port}?`)) {
                          await api.deleteProxy(p.id);
                          await after();
                        }
                      }}
                      className="text-red-600 dark:text-red-400 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div>
        <textarea
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          rows={3}
          placeholder={'Paste proxies, one per line:\nuser:password@host:port\nhost:port'}
          className={`${input} w-full font-mono text-xs`}
        />
        <div className="mt-1 flex items-center gap-3">
          <button
            type="button"
            disabled={!paste.trim()}
            onClick={add}
            className="px-3 py-1.5 bg-gray-900 text-white rounded-md text-xs hover:bg-gray-800 disabled:opacity-50"
          >
            Add to this list
          </button>
          {message && <span className="text-xs text-gray-500 dark:text-gray-400">{message}</span>}
        </div>
      </div>
    </div>
  );
}
